import { useCenteredCard } from '@lib/hooks/CenteredCardContext';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseLibraryCardVideoOptions {
  mediaId: string;
  /** Whether the "Autoplay videos" setting is on. */
  autoplayEnabled: boolean;
}

/**
 * Library-card video lifecycle hook. Owns the `<video>` element ref
 * for a single card and provides two things:
 *
 *   1. `cardRef` + mobile center-line autoplay (plays/pauses based
 *      on proximity to the viewport center on mobile).
 *   2. `pauseVideo()` — an imperative handle used by desktop to
 *      force-pause when a menu/modal opens on top of the card.
 *
 * Keeping both in one hook avoids threading `videoEl` through
 * VideoCard so two hooks could share it.
 */
export function useLibraryCardVideo({ mediaId, autoplayEnabled }: UseLibraryCardVideoOptions) {
  const cardRef = useRef<HTMLDivElement>(null);
  // Callback ref so the play/pause effect re-runs when VideoPreview
  // mounts/remounts the underlying <video> element. A plain useRef
  // wouldn't trigger the effect when the ref first becomes non-null.
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const videoRef = useCallback((el: HTMLVideoElement | null) => setVideoEl(el), []);
  const { playbackId, bandIds, register, isActive: isCenteredCardActive } = useCenteredCard();
  /**
   * Nominated to autoplay. Null for everyone once more than one card is revealed — with a row of
   * controls on screen there is no non-arbitrary card to pick, and several playing at once is worse
   * than none. So this is the single-column centre line and nothing else.
   */
  const isPlaybackTarget = isCenteredCardActive && playbackId === mediaId;
  /** Level with the nominated card, so this card shows its controls. Its whole row does. */
  const isInBand = isCenteredCardActive && bandIds.has(mediaId);

  // Register this card for center-of-viewport tracking.
  useEffect(() => {
    if (!isCenteredCardActive || !cardRef.current) return;
    return register(mediaId, cardRef.current);
  }, [isCenteredCardActive, mediaId, register]);

  // Play/pause based on center proximity using direct video element control.
  // The externallyPlaying prop on VideoPreview handles visual state (thumbnail,
  // paused class).
  useEffect(() => {
    // Whoever owns the reveal owns playback too, so this follows the same flag as registration. On
    // a hover device it stands aside and lets VideoPreview's own hover-play work.
    if (!isCenteredCardActive || !videoEl) return;

    if (!autoplayEnabled) {
      videoEl.pause();
      return;
    }

    if (isPlaybackTarget) {
      // TODO: surface persistent autoplay failures (e.g. via a muted-autoplay fallback)
      const tryPlay = () => {
        // Guard: don't play if the card has scrolled out of view since becoming centered.
        // The canplay event can fire after the IntersectionObserver paused the video
        // but before the pick updates (debounced 150ms), causing off-screen playback.
        if (cardRef.current) {
          const rect = cardRef.current.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        }
        videoEl.play().catch(() => {});
      };
      tryPlay();
      videoEl.addEventListener('canplay', tryPlay, { once: true });
      return () => videoEl.removeEventListener('canplay', tryPlay);
    } else {
      videoEl.pause();
    }
  }, [isCenteredCardActive, isPlaybackTarget, autoplayEnabled, videoEl]);

  const pauseVideo = useCallback(() => {
    videoEl?.pause();
  }, [videoEl]);

  return { cardRef, videoRef, isPlaybackTarget, isInBand, isCenteredCardActive, pauseVideo };
}
